import { Box, ThemeProvider } from '@mui/material';
import { useStyledTheme } from 'api/theme';
import { IS_TEST_MODE } from 'consts';
import { useEffectOnceWhen, useQueryParams } from 'hooks';
import { QueryParamK } from 'keys';
import React, {
	createContext,
	PropsWithChildren,
	useContext,
	useMemo,
	useState
} from 'react';
import { Helmet } from 'react-helmet-async';
import { createStyledDarkTheme } from 'theme/darkTheme';
import { createStyledLightTheme } from 'theme/lightTheme';

interface IModeThemeValue {
	dark: boolean;
	themeLoaded: boolean;
	setDark: React.Dispatch<React.SetStateAction<boolean>>;
}

const ModeTheme = createContext<IModeThemeValue | undefined>(undefined);

interface IModeThemeContextProviderProps extends PropsWithChildren {
	skipThemeLoad?: boolean;
}

// allow other tests to use this provider without rendering the router provider
const useQueryParamsWithTestFallback = (
	key: string
): [string | null, (v: string | null) => void] => {
	try {
		return useQueryParams(key);
	} catch (e) {
		if (IS_TEST_MODE) {
			return [null, () => {}];
		}
		/* istanbul ignore next: wont happen in tests or prod */
		throw e;
	}
};

export const ModeThemeProvider = ({
	children,
	skipThemeLoad = false
}: IModeThemeContextProviderProps) => {
	const prefersDarkColorScheme = window.matchMedia(
		'(prefers-color-scheme: dark)'
	).matches;

	const [providedTheme, setProvidedTheme] = useQueryParamsWithTestFallback(
		QueryParamK.Theme
	);
	let shouldBeDark = prefersDarkColorScheme;
	if (providedTheme === 'dark') {
		shouldBeDark = true;
	} else if (providedTheme === 'light') {
		shouldBeDark = false;
	}
	const [dark, setDark] = useState<boolean>(shouldBeDark);

	const [loadedFonts, setLoadedFonts] = useState<Record<string, boolean>>({});

	const { data: styledThemeData, isError } = useStyledTheme(!skipThemeLoad);

	const fontsUrlsToLoad = useMemo(() => {
		if (styledThemeData === undefined) {
			return null;
		}
		const seen: Record<string, boolean> = {};

		return [
			styledThemeData?.light?.fonts?.font1?.url,
			styledThemeData?.light?.fonts?.font2?.url,
			styledThemeData?.dark?.fonts?.font1?.url,
			styledThemeData?.dark?.fonts?.font2?.url
		]
			.filter(Boolean)
			.filter((url) => {
				if (seen[url!]) {
					return false;
				}
				seen[url!] = true;
				return true;
			}) as string[];
	}, [styledThemeData]);

	const value = useMemo(
		() => ({
			dark,
			setDark,
			themeLoaded:
				skipThemeLoad ||
				isError ||
				(Array.isArray(fontsUrlsToLoad) &&
					fontsUrlsToLoad.every((url) => loadedFonts[url]))
		}),
		[dark, fontsUrlsToLoad, isError, loadedFonts, skipThemeLoad]
	);

	const lightTheme = useMemo(
		() => createStyledLightTheme(styledThemeData),
		[styledThemeData]
	);
	const darkTheme = useMemo(
		() => createStyledDarkTheme(styledThemeData),
		[styledThemeData]
	);

	const theme = useMemo(
		() => (dark ? darkTheme : lightTheme),
		[dark, darkTheme, lightTheme]
	);

	const faviconUrl = prefersDarkColorScheme
		? darkTheme.assets.favicon
		: lightTheme.assets.favicon;

	useEffectOnceWhen(
		!!providedTheme,
		() => {
			(async () => {
				setProvidedTheme(null);
			})();
		},
		[setProvidedTheme]
	);

	return (
		<ModeTheme.Provider value={value}>
			{fontsUrlsToLoad?.map((url) => (
				<link
					key={url}
					href={url}
					data-testid="font-load-script"
					rel="stylesheet"
					type="text/css"
					onLoad={() => setLoadedFonts((val) => ({ [url!]: true, ...val }))}
				/>
			))}
			{APP_MODE === 'app' && theme.assets.favicon && (
				<Helmet>
					<link rel="icon" type="image/png" href={faviconUrl} sizes="16x16" />
				</Helmet>
			)}
			<ThemeProvider theme={theme}>
				<Box
					data-testid="grayscale-overlay"
					position="absolute"
					top={0}
					left={0}
					width="100%"
					height="100%"
					sx={{
						mixBlendMode: 'color',
						zIndex: theme.zIndex.drawer + 1,
						transition: 'background-color .7s linear, visibility .7s',
						backgroundColor: !value.themeLoaded ? 'black' : 'transparent',
						visibility: !value.themeLoaded ? 'visible' : 'hidden',
						pointerEvents: 'none'
					}}
				/>
				{children}
			</ThemeProvider>
		</ModeTheme.Provider>
	);
};

export const useModeTheme = (): IModeThemeValue => {
	const context = useContext(ModeTheme);
	if (!context) throw new Error('ModeThemeProvider is missing');
	return context;
};
